import { Injectable, inject } from "@angular/core";
import { Observable } from "rxjs";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { HandelssortimentsgruppDTO } from "~app/common/models/common/HandelssortimentsgruppDTO";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { ApiClientService } from "~app/common/services/api-client.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { HandelssortimentTreeType, KundorderListRequestDTO } from "~app/features/forsaljning/models/KundorderListRequestDTO";
import { KundorderListResponseDTO } from "~app/features/forsaljning/models/KundorderListResponseDTO";
import { TerminalorderBasDataDTO } from "~app/features/forsaljning/terminalorder/models/TerminalorderBasDataDTO";

@Injectable({
    providedIn: "root"
})
export class TerminalorderService {
    private readonly apiClient = inject(ApiClientService);

    private readonly handelssortimentType = {
        group: "HG",
        handelssortiment: "HS"
    };

    private readonly selectedLevel = {
        none: "none",
        partial: "partial",
        all: "all"
    };

    search(request: KundorderListRequestDTO): Observable<KundorderListResponseDTO[]> {
        request.kundordertyp = "TERMINAL";
        return this.apiClient.post<KundorderListRequestDTO, KundorderListResponseDTO[]>("kundorder/search", request);
    }

    getTerminalTypeAhead(input: string): Observable<ITypeAheadResult[]> {
        return this.apiClient.post<any, ITypeAheadResult[]>(`typeahead/terminal`, `"${input}"`, false, null, ApiClientService.JsonHeader);
    }

    getTerminalorderBasData(): Observable<TerminalorderBasDataDTO> {
        return this.apiClient.get<TerminalorderBasDataDTO>("terminalorder/basdata");
    }

    setupHandelssortimentTreeSelector(handelssortiment: HandelssortimentsgruppDTO[], valdaHs: HandelssortimentTreeType[]): SelectionItem[] {
        const response: SelectionItem[] = [];

        handelssortiment.forEach(hs => {
            response.push(this.createTreeSelectionNode(hs, valdaHs));
        });

        this.setPartialSelected(response);

        return response;
    }

    getSelectedHandelssortiment(handelssortimentsgrupper: SelectionItem[]) {
        const data = [];
        handelssortimentsgrupper.forEach(hsg => {
            if (hsg.selected) {
                data.push( {
                    typ: this.handelssortimentType.group,
                    id: hsg.value
                });
            }

            if (hsg.selected || hsg.partialSelected) {
                hsg.children.forEach(hs => {
                    if (hs.selected) {
                        data.push( {
                            typ: this.handelssortimentType.handelssortiment,
                            id: hs.value
                        });
                    }
                });
            }
        });

        return data;
    }

    private setPartialSelected(array: SelectionItem[]): SelectionItem[] {
        array.forEach(a => {
            if (!ObjectUtils.isNullOrUndefined(a.children)) {
                const res = this.setSelectedChildren(a.children);
                if (res === this.selectedLevel.all) {
                    a.selected = true;
                } else if (res ===  this.selectedLevel.partial) {
                    a.partialSelected = true;
                }
            }
        });

        return array;
    }

    private createTreeSelectionNode(node: HandelssortimentsgruppDTO, valdaHs: HandelssortimentTreeType[]) {
        let children: SelectionItem[] = null;
        if (!ObjectUtils.isNullOrUndefined(node.children)) {
            children = children === null ? [] : children;
            node.children.forEach((data: any) => {
                children.push(this.createTreeSelectionNode(data, valdaHs));
            });

        }

        return {
            value: node.id,
            text: node.namn,
            selected: this.setNodeSelection(node, valdaHs),
            partialSelected: false,
            children: children
        } as SelectionItem;
    }

    private setNodeSelection(node: HandelssortimentsgruppDTO, valdaHs: HandelssortimentTreeType[]): boolean {
        let res = false;
        try {
            valdaHs?.forEach((grupp) => {
                if (grupp.id === node.id &&
                    (grupp.typ === this.handelssortimentType.group && !ObjectUtils.isNullOrUndefined(node.children) ||
                    grupp.typ === this.handelssortimentType.handelssortiment && ObjectUtils.isNullOrUndefined(node.children))) {
                    res = true;
                }
            });

        } catch (err: any) {
            return res;
        }

        return res;
    }

    private setSelectedChildren(array: SelectionItem[]): string {
        let numberOfSelected = 0;
        let isPartial = false;
        array.forEach(a => {
            if (!a.selected && !ObjectUtils.isNullOrUndefined(a.children)) {
                const res = this.setSelectedChildren(a.children);
                if (res === this.selectedLevel.all) {
                    a.selected = true;
                } else if (res === this.selectedLevel.partial) {
                    a.partialSelected = true;
                    isPartial = true;
                }
            } else if (a.selected === true) {
                numberOfSelected++;
            }
        });
        if (numberOfSelected === 0 && !isPartial) {
            return this.selectedLevel.none;
        } else if (numberOfSelected === array.length) {
            return this.selectedLevel.all;
        } else if (isPartial || (numberOfSelected < array.length && numberOfSelected > 0)) {
            return this.selectedLevel.partial;
        }

        return this.selectedLevel.none;
    }
}
